import { Box, Checkbox, HStack, Text } from "@chakra-ui/react";
import { SystemStyleObject } from "@chakra-ui/styled-system";
import { dataAttr } from "@chakra-ui/utils";
import {
  chakraComponents,
  ClearIndicatorProps,
  DropdownIndicatorProps,
  GroupBase,
  MultiValueProps,
  OptionProps,
} from "chakra-react-select";
import { first } from "lodash-es";
import React from "react";
import { useTranslation } from "react-i18next";
import { InputProps } from "react-select";
import { SelectComponentsConfig } from "react-select/dist/declarations/src/components";
import { InputElementButton } from "shared/components/input/InputElementButton";
import { SelectBaseProps } from "shared/components/input/select/SelectInternal";
import { SelectOptionData } from "shared/components/input/select/SelectTypes";
import { isSelectNewOptionData } from "shared/components/input/select/SelectUtils";
import { SimpleSelectProps } from "shared/components/input/select/SimpleSelect";
import { ArrowDropDownIcon } from "shared/icons/ArrowDropDownIcon";
import { ArrowRightCircleIcon } from "shared/icons/ArrowRightCircleIcon";
import { CheckIcon } from "shared/icons/CheckIcon";
import { InputXMarkIcon } from "shared/icons/InputXMarkIcon";

const ClearIndicator = (props: ClearIndicatorProps<SelectOptionData>) => {
  const {
    className,
    cx,
    innerProps,
    selectProps: { chakraStyles },
  } = props;
  const { t } = useTranslation(["common"]);
  return (
    <InputElementButton
      aria-label={t("common:clear")}
      aria-hidden={innerProps["aria-hidden"]}
      sx={chakraStyles?.clearIndicator?.({}, props)}
      className={cx(
        {
          indicator: true,
          "clear-indicator": true,
        },
        className,
      )}
      onClickCapture={() => {
        props.clearValue();
        props.selectProps.onInputChange("", {
          action: "set-value",
          prevInputValue: props.selectProps.inputValue,
        });
      }}
      icon={<InputXMarkIcon w="24px" h="24px" color="gray.400" />}
    />
  );
};

const DropdownIndicator: React.FC<DropdownIndicatorProps<SelectOptionData>> = (
  props: DropdownIndicatorProps<SelectOptionData>,
): React.ReactElement => (
  <chakraComponents.DropdownIndicator {...props}>
    <ArrowDropDownIcon
      transform={
        props.selectProps.menuIsOpen ? "rotate(-180deg)" : "rotate(0deg)"
      }
      transitionProperty="transform"
      transitionDuration="slow"
    />
  </chakraComponents.DropdownIndicator>
);

const Input: React.FC<InputProps<SelectOptionData>> = (props) => {
  const { t } = useTranslation(["common"]);
  // Not using built-in placeholder because we want it to be visible
  // any time select has focus, not just when there is no value.
  return (
    <>
      {props.selectProps.isSearchable && !props.value && (
        <Box
          id={props["aria-describedby"]}
          className="react-select__search-placeholder"
          gridArea="1/2/2/3"
          sx={props.selectProps.chakraStyles?.placeholder?.(
            {},
            {
              ...props,
              isFocused: false,
              innerProps: {},
              children: null,
              isDisabled: false,
            },
          )}
        >
          {(props.selectProps as unknown as SelectBaseProps)
            .searchPlaceholder ?? t("common:search")}
        </Box>
      )}
      <chakraComponents.Input
        {...props}
        data-value={
          props.getValue().map(props.selectProps.getOptionValue).join(",") ||
          undefined
        }
        data-hidden={dataAttr(props.isHidden)}
      />
    </>
  );
};

const MultiValue: React.FC<
  MultiValueProps<SelectOptionData> & {
    selectProps: SimpleSelectProps;
  }
> = (props) => {
  const { t } = useTranslation(["common"]);
  const value = props.getValue();
  const count = value.length;
  const containerSx: SystemStyleObject =
    props.selectProps.chakraStyles?.multiValue?.({}, props) ?? {};

  return props.index === 0 ? (
    <Box sx={containerSx} className="react-select__multi-value">
      {count === props.options.length && count > 1
        ? props.selectProps.defaultAllText || t("common:all")
        : count === 1
          ? first(value)?.label
          : t("common:nSelected", { count })}
    </Box>
  ) : null;
};

const Option = <
  IsMulti extends boolean,
  Group extends GroupBase<SelectOptionData>,
>(
  props: OptionProps<SelectOptionData, IsMulti, Group>,
): React.ReactElement =>
  isSelectNewOptionData(props.data) ? (
    <chakraComponents.Option
      {...props}
      className="react-select__option--is-new"
    >
      <Text flexGrow="1" textStyle="bold">
        {props.data.label}
      </Text>
      <ArrowRightCircleIcon w="16px" h="16px" />
    </chakraComponents.Option>
  ) : (
    <chakraComponents.Option {...props}>
      {props.isMulti && (
        <Checkbox pointerEvents="none" isChecked={props.isSelected} />
      )}
      <HStack
        flex={1}
        overflow="hidden"
        textOverflow="ellipsis"
        justifyContent="space-between"
      >
        <Text style={props.data.labelStyles}>{props.data.label}</Text>
        {props.data.labelRight ? (
          <Text textStyle="small" color="gray.500">
            {props.data.labelRight}
          </Text>
        ) : null}
        {props.data.rightChild ? props.data.rightChild : null}
      </HStack>
      {!props.isMulti && props.isSelected ? (
        <CheckIcon w="20px" h="20px" color="primary.500" mr="4px" />
      ) : null}
      {!props.isMulti && !props.isSelected ? <Box w="20px" /> : null}
    </chakraComponents.Option>
  );

export const selectComponentsConfig: SelectComponentsConfig<
  SelectOptionData,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
> = {
  ClearIndicator,
  DropdownIndicator,
  Input,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  MultiValue: MultiValue as any,
  Option,
};
